import { Slot } from 'radix-ui';
import type * as React from 'react';
import { cn } from '@/shared/lib/cn';

type CardProps = React.ComponentProps<'div'> & {
  /** `compact` tightens padding for the workspace density. */
  size?: 'default' | 'compact';
};

/** White 12px-radius surface with the card shadow. Compose with the `Card*` parts. */
function Card({ className, size = 'default', ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        'group/card flex flex-col gap-4 rounded-lg border bg-card py-6 text-card-foreground shadow-card data-[size=compact]:gap-3 data-[size=compact]:py-4',
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'grid auto-rows-min grid-cols-[minmax(0,1fr)] items-start gap-1 px-6 group-data-[size=compact]/card:px-4 sm:has-data-[slot=card-action]:grid-cols-[minmax(0,1fr)_auto]',
        className,
      )}
      {...props}
    />
  );
}

/** Entity title in Sectra. An `h3` unless `asChild` supplies another heading level. */
function CardTitle({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'h3'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'h3';
  return (
    <Comp
      data-slot="card-title"
      className={cn(
        'font-display text-xl text-heading group-data-[size=compact]/card:font-sans group-data-[size=compact]/card:text-base',
        className,
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="card-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'mt-2 sm:col-start-2 sm:row-span-2 sm:row-start-1 sm:mt-0 sm:self-start sm:justify-self-end',
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-6 group-data-[size=compact]/card:px-4', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'flex flex-wrap items-center gap-2 px-6 group-data-[size=compact]/card:px-4',
        className,
      )}
      {...props}
    />
  );
}

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
